// Every server value the follow-up pages show, worded for the owner in one place (spec:
// duty-follow-up-messages, w-admin design "Wording").
import { labelFor } from '../../../utils/wireKeys'

export const KIND_ORDER = [
  'after_delivery',
  'after_delivery_dealer',
  'dealer_invite',
  'paywall_dropoff',
  // Last, because it is the only one that is not about the duty check that just happened: it goes
  // out a fortnight later (spec: whatsapp-shipment-tracking Req 10).
  'tracking_invite'
]

// When each message goes out, and to whom — the line under each card's title.
export const KIND_WHEN = {
  after_delivery:
    'Sent once, after the last car is delivered. To customers who are not registered dealers.',
  after_delivery_dealer:
    'Sent instead of the message above when the customer is a registered dealer.',
  dealer_invite:
    'Sent instead of the first message to a busy customer who is not a dealer yet. Has two buttons: "Yes, set me up" and "No thanks".',
  paywall_dropoff:
    'Sent once per car when a customer goes quiet at a payment question before paying. Includes a card link for those without MoMo.',
  tracking_invite:
    'Sent two weeks after a duty check, asking whether the car is on the ship yet. Has one button: "Track my shipment". Needs the approved WhatsApp template, because by then the free 24-hour window has closed.'
}

export const SKIP_REASONS = {
  disabled: 'Switched off',
  variant_disabled: 'Switched off',
  internal: 'Our own number',
  new_check: 'Started another check',
  cooldown: 'Already messaged recently',
  outside_window: 'Outside WhatsApp\'s 24-hour window',
  answered: 'Replied before it was due',
  not_awaiting_payment: 'No longer at the payment step',
  charge_exists: 'Already paying',
  superseded: 'Replaced by a later question',
  already_sent: 'Already sent for this car',
  // The tracking invitation's own three (spec: whatsapp-shipment-tracking Req 10.2).
  already_tracking: 'Already tracking a shipment',
  tracking_unavailable: 'Tracking could not be sold',
  no_template: 'The WhatsApp template is not set up'
}

export const skipReasonLabel = reason => labelFor(SKIP_REASONS, reason) || reason

export const RAIL_LABELS = { momo: 'MoMo', card: 'Card', merchant: 'Merchant MoMo' }

// The six timings and audience rules, with the range the API accepts.
export const RULES = [
  {
    field: 'followupAfterDeliveryMinutes',
    label: 'After-delivery delay',
    unit: 'minutes',
    min: 5,
    max: 240,
    help: 'Sent this long after the customer\'s last car is delivered. People often check one car after another, so too short catches them mid-check.'
  },
  {
    field: 'followupCooldownDays',
    label: 'Once every',
    unit: 'days',
    min: 1,
    max: 365,
    help: 'A customer gets the after-delivery message at most once in this many days.'
  },
  {
    field: 'dealerInviteMinPaidChecks',
    label: 'Busy customer: at least',
    unit: 'paid checks',
    min: 2,
    max: 50,
    help: 'A customer who is not a registered dealer and has paid for at least this many checks…'
  },
  {
    field: 'dealerInviteWindowDays',
    label: '…within',
    unit: 'days',
    min: 7,
    max: 365,
    help: '…in this many days is invited to become a dealer.'
  },
  {
    field: 'paywallDropoffMinutes',
    label: 'Drop-off delay',
    unit: 'minutes',
    min: 5,
    max: 720,
    help: 'Sent this long after a payment question goes unanswered. At most 12 hours, so it never falls outside WhatsApp’s 24-hour window.'
  },
  {
    field: 'followupTrackingInviteDays',
    label: 'Tracking invitation after',
    unit: 'days',
    min: 7,
    max: 90,
    help: 'Sent this long after a duty check, asking for the bill of lading. Fourteen days is when a car is usually at sea: of the customers who write again, most do so between two weeks and three months later.'
  }
]

// The server's own sentence for a refused request (memory: w-admin-conventions).
export const serverSentence = response =>
  response?.error?.response?.data?.error ||
  response?.error?.response?.data?.detail ||
  ''
